import type { LearnTopic } from "../topic-types";

/**
 * Working With Your Inner Critic.
 *
 * Add new guides, exercises, prompts or worksheets by appending to a
 * section's `resources` array — no page or route changes required.
 */
export const INNER_CRITIC_TOPIC: LearnTopic = {
  slug: "inner-critic",
  emoji: "🪞",
  title: "Working With Your Inner Critic",
  tagline: "Self-compassion instead of self-attack",
  theme: "peach",
  category: "shame-self-worth",
  seoDescription:
    "Understand where a harsh inner critic can come from, learn to separate critical thoughts from facts, and practise more compassionate internal language. Trauma-informed guides, exercises and worksheets from Recovery Trauma™.",
  cardBlurb: "Where the harsh inner voice comes from, and how to soften it.",
  isNew: true,
  intro: [
    "Many people carry an inner voice that is far harder on them than they would ever be on someone else — quick to criticise, slow to forgive, and rarely satisfied. This voice can feel like simply “how things are”, but it usually has a history.",
    "For people who have lived through difficult or unsafe experiences, a harsh inner critic often developed as a kind of protection — a way of staying one step ahead of criticism, rejection or failure from the outside. It is closely linked to shame, which is why this module works alongside our Shame-Based Trauma guide.",
    "This is not about silencing the critic overnight, or replacing it with forced positivity. It is about noticing the voice, questioning what it says, and slowly building a kinder, more accurate way of talking to yourself.",
  ],
  closing:
    "An inner critic that formed to protect you is not a flaw to eliminate — it is a pattern to understand and gradually soften. If self-critical thoughts are relentless, feel unsafe, or tip into thoughts of harming yourself, please see our Self-Harm Support and Crisis Resources pages, and consider talking to a GP or trauma-informed therapist.",
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understanding the inner critic",
      blurb: "Where harsh self-talk often comes from, and why it can feel so convincing.",
      intro:
        "Plain-English education first. Understanding where this voice came from can make it easier to meet with curiosity rather than more self-attack.",
      resources: [
        {
          slug: "where-it-comes-from",
          kind: "learn",
          title: "Where harsh self-talk can come from",
          summary: "Early experiences, internalised criticism, and protection.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "An inner critic rarely appears from nowhere. For many people it forms out of messages received early in life — from parents, teachers, peers or wider culture — that were critical, conditional, or demanding of perfection. Over time, those outside voices can become an inside one.",
            },
            {
              type: "list",
              title: "Common origins people recognise",
              items: [
                "Growing up around frequent criticism, comparison or high expectations",
                "Love or approval that felt conditional on achievement or behaviour",
                "Being blamed for things outside your control",
                "Environments where mistakes were met with anger, ridicule or withdrawal",
                "Learning that criticising yourself first felt safer than being caught off guard by someone else's criticism",
              ],
            },
            {
              type: "note",
              text: "None of this means your inner critic is “your fault”, and it doesn't mean the people around you were always to blame either — it is simply one way a nervous system tries to keep you safe.",
            },
          ],
        },
        {
          slug: "critic-as-protector",
          kind: "learn",
          title: "The inner critic as a protector",
          summary: "Why a harsh inner voice can actually be trying to help.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "It can feel strange to hear, but an inner critic is very often trying to protect you — from rejection, from failure, from being caught unprepared, or from repeating a painful experience. Getting there first, with criticism, can feel safer than being surprised by someone else's disapproval.",
            },
            {
              type: "flow",
              title: "A pattern that often develops",
              items: [
                "Something painful happens (criticism, rejection, punishment)",
                "A part of you learns: “if I criticise myself first, I can avoid this”",
                "The critic becomes constant, not just occasional",
                "It keeps working even in situations that are actually safe",
              ],
            },
            {
              type: "prose",
              text: "Understanding the critic's intention doesn't make its words true or kind — but it can shift your relationship with it, from being at war with a part of yourself to being curious about what it's trying to do.",
            },
          ],
        },
        {
          slug: "critic-and-shame",
          kind: "learn",
          title: "The inner critic and shame",
          summary: "How self-attack and shame can reinforce each other.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Shame is the feeling that something is wrong with you as a person, rather than with something you did. A harsh inner critic often speaks in exactly that language — “you're stupid”, “you're too much”, “you're unlovable” — rather than commenting on a specific action.",
            },
            {
              type: "list",
              title: "How the cycle can look",
              items: [
                "A mistake or setback happens",
                "The inner critic responds with global, personal attacks",
                "Shame intensifies, making the critic feel even more “true”",
                "Withdrawal, over-apologising or self-punishment can follow",
                "The pattern repeats next time something goes wrong",
              ],
            },
            {
              type: "prose",
              text: "Because shame and self-criticism are so closely linked, it can help to explore this module alongside our dedicated Shame-Based Trauma guide, which looks in more depth at where shame comes from and how to work with it.",
            },
          ],
        },
        {
          slug: "perfectionism",
          kind: "learn",
          title: "Perfectionism and internalised criticism",
          summary: "When the critic disguises itself as “high standards”.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Perfectionism can look like ambition or diligence from the outside, but underneath it is often the inner critic in disguise — insisting that being good enough is never quite enough, and that mistakes are dangerous rather than ordinary.",
            },
            {
              type: "list",
              title: "Signs perfectionism may be critic-driven",
              items: [
                "Finished work rarely feels “done” or good enough",
                "Mistakes trigger a disproportionate sense of failure or shame",
                "Rest feels unsafe unless it has been fully earned",
                "Praise is deflected or quickly forgotten; criticism is remembered for years",
                "Self-worth seems to rise and fall with performance",
              ],
            },
            {
              type: "note",
              text: "High standards aren't the problem in themselves — it's when they are enforced through fear and self-attack, rather than care, that they start to cost you.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔎",
      title: "Recognising the critic",
      blurb: "Noticing critical thoughts as they arise, and separating them from fact.",
      intro:
        "This section moves from reading to noticing. You don't need to catch every critical thought — even noticing a few, some of the time, is real progress.",
      resources: [
        {
          slug: "recognising-critical-thoughts",
          kind: "learn",
          title: "Recognising critical thoughts",
          summary: "What the inner critic tends to sound like.",
          minutes: 3,
          blocks: [
            {
              type: "list",
              title: "Common patterns of critical thinking",
              items: [
                "All-or-nothing language: “always”, “never”, “completely failed”",
                "Global attacks on your character rather than a specific action: “I'm so stupid” rather than “that was a mistake”",
                "Mind-reading: assuming others are judging you harshly",
                "Comparison: measuring yourself against others and always coming up short",
                "Predicting failure before you've even started",
              ],
            },
            {
              type: "prose",
              text: "You might notice the critic has a particular tone or even sounds like a specific person from your past. That's worth noting gently, without needing to do anything about it yet.",
            },
          ],
        },
        {
          slug: "thought-vs-fact",
          kind: "practice",
          title: "Thought vs fact: separating what you think from what is true",
          summary: "A practice for checking a critical thought against actual evidence.",
          minutes: 8,
          blocks: [
            {
              type: "prose",
              text: "Critical thoughts often feel like facts, especially when they're familiar. This practice slows things down enough to ask: is this a thought, or is this evidence?",
            },
            {
              type: "steps",
              title: "How to try it",
              items: [
                { label: "Name the thought", note: "Write it exactly as it appeared, e.g. “I always mess everything up.”" },
                { label: "Ask: is this a thought or a fact?", note: "Facts can be observed by anyone; thoughts are interpretations." },
                { label: "List the actual evidence", note: "What specifically happened? Just the observable facts, no judgement words." },
                { label: "List evidence against the thought", note: "Times this hasn't been true, even small ones." },
                { label: "Write a more balanced statement", note: "One that fits the evidence, not just the critic's version." },
              ],
            },
            {
              type: "note",
              text: "This is a thinking-skills exercise, not a way to argue yourself out of real feelings. Feelings can still be valid even when a thought turns out to be inaccurate.",
            },
          ],
        },
        {
          slug: "noticing-the-triggers",
          kind: "reflect",
          title: "Noticing what triggers the critic",
          summary: "Tracing the situations where the critic gets loudest.",
          minutes: 5,
          blocks: [
            {
              type: "prompts",
              items: [
                "When did I hear the inner critic most recently?",
                "What was happening just before it spoke up?",
                "Whose voice or tone does it remind me of, if any?",
                "What is it usually trying to prevent — failure, rejection, disappointing someone?",
                "How does my body feel when the critic is active?",
              ],
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practising a kinder inner voice",
      blurb: "Building more compassionate internal language, step by step.",
      intro:
        "These exercises are optional, and there is no “correct” pace. You can stop at any point — stopping is a valid outcome, not a failure.",
      resources: [
        {
          slug: "compassionate-rewrite",
          kind: "practice",
          title: "The compassionate rewrite",
          summary: "Turning a critical thought into something more accurate and kind, with worked examples.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "This exercise doesn't ask you to believe something rosy or untrue. It asks: what would a fair, caring observer — someone who wants the best for you and also tells the truth — actually say here?",
            },
            {
              type: "steps",
              title: "How to rewrite a critical thought",
              items: [
                { label: "Write the critical thought", note: "In its full, unfiltered form." },
                { label: "Notice the tone", note: "Harsh, mocking, contemptuous, cold?" },
                { label: "Ask what a compassionate but honest friend would say", note: "Not empty praise — a fair, accurate response." },
                { label: "Rewrite it in that voice", note: "Keep it specific and believable, not just “positive”." },
              ],
            },
            {
              type: "list",
              title: "Worked examples",
              items: [
                "“I'm so stupid, I can't believe I said that” → “That came out awkwardly, and that's uncomfortable — it doesn't mean I'm stupid. People misspeak.”",
                "“I always ruin everything” → “This particular thing went wrong. I can look at what happened without deciding it says something permanent about me.”",
                "“No one actually likes me” → “I don't have proof of that. I feel insecure right now, and that feeling is understandable, even if it isn't the full picture.”",
                "“I should have known better” → “I made a decision with the information I had at the time. Hindsight makes it look more obvious than it was.”",
              ],
            },
            {
              type: "note",
              text: "If a fully compassionate rewrite feels impossible right now, try for “slightly less harsh and more accurate” instead. Small shifts count.",
            },
          ],
        },
        {
          slug: "responding-to-the-critic",
          kind: "practice",
          title: "Responding to the critic in the moment",
          summary: "A brief in-the-moment practice for when the critic speaks up.",
          minutes: 4,
          blocks: [
            {
              type: "steps",
              items: [
                { label: "Pause and notice", note: "“There's the critic again.”" },
                { label: "Name its intention", note: "“It's probably trying to protect me from something.”" },
                { label: "Offer a brief, honest response", note: "“Thank you for trying to help. I've got this from here.”" },
                { label: "Return attention to the present task or moment", note: "You don't need to resolve the critic fully to move on." },
              ],
            },
          ],
        },
        {
          slug: "inner-critic-worksheet",
          kind: "worksheet",
          title: "Inner critic worksheet",
          summary: "A printable page to work through a critical thought at your own pace.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "Use this whenever a critical thought feels loud enough to be worth pausing for. There's no need to fill it in perfectly — partial answers are still useful.",
            },
          ],
          worksheetFields: [
            { label: "The critical thought, word for word", lines: 2 },
            { label: "Whose voice or tone does this remind me of, if any?", lines: 2 },
            { label: "What is the actual evidence for this?", lines: 3 },
            { label: "What is the evidence against it?", lines: 3 },
            { label: "What might this part of me be trying to protect me from?", lines: 2 },
            { label: "A more balanced, accurate statement", lines: 3 },
            { label: "One kind thing I could say to myself right now", lines: 2 },
          ],
        },
      ],
    },
    {
      slug: "reflect",
      emoji: "✍️",
      title: "Reflect",
      blurb: "Journal prompts for exploring your relationship with your inner critic over time.",
      resources: [
        {
          slug: "reflecting-on-the-critic",
          kind: "reflect",
          title: "Your relationship with your inner critic",
          summary: "Longer-term reflection prompts.",
          minutes: 8,
          blocks: [
            {
              type: "prompts",
              items: [
                "How long have I known this voice? Can I remember when it first got loud?",
                "What has the critic been trying to protect me from all this time?",
                "What would change in my life if the critic were 10% quieter?",
                "Who in my life speaks to me in a way I'd like to learn from?",
                "What is one piece of evidence, from this week, that the critic got something wrong?",
              ],
            },
            {
              type: "quote",
              text: "You can question a thought without needing to win an argument against yourself.",
            },
          ],
        },
      ],
    },
  ],
  continueTo: [
    { label: "Shame-Based Trauma", to: "/learn/shame-based-trauma" },
    { label: "CBT Skills", to: "/learn/cbt" },
    { label: "Inner Child", to: "/learn/inner-child" },
    { label: "Journal", to: "/journal" },
  ],
};
